import React, { useState, useRef, useMemo, MouseEventHandler, forwardRef, Ref, useEffect, RefObject } from 'react'
import cn from 'classnames'

import Icon from '../Icon2/Icon'
import Button from '../Button/Button'

import './PageLayout.css'
import { isEnterOrSpacePressed, isArrowRightPressed, isArrowLeftPressed, isArrowUpPressed, isArrowDownPressed } from 'utils/keyboardUtils'

export type headerProps = {
  backNavigation?: Function
  Breadcrumb?: React.ReactNode
  component?: React.ReactNode
  title?: string
  headerLogoOnClick?: MouseEventHandler
}

export type mainContentProps = {
  component?: React.ReactNode
  livePreview?: React.ReactNode
}

export type sidebarProps = {
  component?: React.ReactNode
}

export type topNavBarProps = {
  component?: React.ReactNode
}

export type stickyFooterProps = {
  component?: React.ReactNode
}

export type layoutProps = {
  footer?: stickyFooterProps
  content?: mainContentProps | emptyPageContentProps
  header?: headerProps
  headerType?: string
  leftSidebar?: sidebarProps
  rightSidebar?: sidebarProps
  layoutBackgroundColor?: boolean
  leftFooter?: React.ReactNode
  rightFooter?: React.ReactNode
  contentHeader?: React.ReactNode
  noSideNav?: boolean
  type: string
  className?: string
  mode?: 'compact' | 'fullscreen'
  topNavBar?: topNavBarProps
  topNavMode?: boolean
  version?: 'v2'
  hasBackground?: boolean
  testId?: string,
  resizeable?: boolean,
  resizableLeftMaxWidth?: number
  resizableLeftMinWidth?: number
  initialWidth?: number
  setCurrentWidth?: Function
  noLeftNav?: boolean,
  breadcrumbMargin?: boolean
}

export type contentProps = {
  content: mainContentProps
  type: string
  toggle?: Function
  toggleStat?: boolean
  headerType?: string
  mode?: 'compact' | 'fullscreen'
  layoutBackgroundColor?: boolean
  onKeyDown?: any
  version?: string
  resizeable?: boolean
  resizableLeftMaxWidth?: number
  resizableLeftMinWidth?: number
  layoutHeaderRef?: RefObject<HTMLInputElement>
  pageLayoutRef?: RefObject<HTMLInputElement>
  leftSidebarRef?: RefObject<HTMLInputElement>
  setCurrentWidth?: Function
  noLeftNav?: boolean
  noSideNav?: boolean
}

export type layoutHeaderProps = {
  header: headerProps
  toggle?: Function
  toggleStat?: boolean
  type: string
  headerType?: string
  mode?: 'compact' | 'fullscreen'
  layoutBackgroundColor?: boolean
  onKeyDown?: any
  testId?: string
  version?: string
  noLeftNav?: boolean
}

export type leftSidebarProps = {
  leftSidebar: sidebarProps
}

export type rightSidebarProps = {
  rightSidebar: sidebarProps
}

export type footerProps = {
  footer?: stickyFooterProps
  onKeyDown?: any
}

export type rightFooterProps = {
  rightFooter?: React.ReactNode
  onKeyDown?: any
}

export type leftFooterProps = {
  leftFooter?: React.ReactNode
  onKeyDown?: any
}

export type secondaryLayoutProps = {
  content: mainContentProps
  footer?: stickyFooterProps
  header: headerProps
  leftSidebar?: sidebarProps
  rightSidebar?: sidebarProps
  className?: string
  mode?: 'compact' | 'fullscreen'
  layoutBackgroundColor?: boolean
  leftFooter?: React.ReactNode
  rightFooter?: React.ReactNode
  contentHeader?: React.ReactNode
  topNavBar?: topNavBarProps
  version?: 'v2'
  testId?: string
  noLeftNav?: boolean
  noSideNav?: boolean,
  breadcrumbMargin?: boolean
}

export type primaryLayoutProps = {
  content: mainContentProps
  header: headerProps
  leftSidebar?: sidebarProps
  type?: string
  headerType?: string
  className?: string
  hasBackground?: boolean
  noSideNav?: boolean
  testId?: string
  version?: string
  resizeable?: boolean,
  resizableLeftMaxWidth?: number
  resizableLeftMinWidth?: number,
  initialWidth?: number
  setCurrentWidth?: Function
  noLeftNav?: boolean,
  breadcrumbMargin?: boolean
}

export type emptyPageContentProps = {
  component?: React.ReactNode
  alignContent?: 'center'
  backgroundColor?: 'primary' | 'secondary'
}

export type emptyPageProps = {
  content: emptyPageContentProps
  header?: headerProps
  testId?: string
}

const getKeyboardFocusableElements = (element = document) => {
  const elements = element.querySelectorAll(
    'a[href], button, input, textarea, select, details,[tabindex]:not([tabindex="-1"])'
  )
  return Array.from(elements).filter(
    el => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden')
  )
}


const setRequiredFocus = (currentRef: React.MutableRefObject<any>) => {
  const items = getKeyboardFocusableElements(currentRef.current)
  if (items.length) {
    (items[0] as HTMLElement).focus()
  }
  else {
    currentRef?.current?.focus()
  }
}

function pxToRem(pxValue) {
  // Get the root font size
  var rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;

  // Convert px to rem
  var remValue = pxValue / rootFontSize;

  return remValue;
}

function remToPx(rem) {
  return rem * parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
}

const TopNavbar = ({ topNavBar }: { topNavBar: topNavBarProps }) => {
  return <>{topNavBar.component}</>
}

const LayoutHeader = forwardRef((props: layoutHeaderProps, layoutHeaderRef: Ref<any>) => {
  let headClassname = ''
  if (props.type === 'card') {
    headClassname = props.headerType === 'custom' ? 'PageLayout__head--card--custom-header' : 'PageLayout__head--card'
  }
  return useMemo(() => {
    if (props.header !== undefined) {
      return (
        <div onKeyDown={props.onKeyDown} ref={layoutHeaderRef} tabIndex={-1} className={`PageLayout__head ${headClassname}`} data-test-id="cs-page-layout-header">
          {props.toggleStat !== undefined ? (
            !props.noLeftNav && <div
              className="PageLayout__head__hamburger"
              data-test-id={`${props.testId}-hamburger-menu-${props.toggleStat ? "open" : "close"}`}
              id="PageLayout-hamburger"
              onClick={() => props.toggle()}
              tabIndex={0}
              onKeyDown={(e: any) => {
                if (isEnterOrSpacePressed(e.key)) {
                  e.preventDefault()
                  e.stopPropagation()
                  props.toggle(e)
                }
              }}>
              {props.version === "v2" ? <Icon icon="CaretLeft" size="tiny" version='v2' /> : <Icon icon="Hamburger" size="original" />}
            </div>
          ) : (
            // <div
            //   data-test-id="cs-page-layout-go-back"
            //   ref={layoutHeaderRef}
            //   className="PageLayout__head__backArrow"
            //   tabIndex={0}
            //   onKeyDown={(e: any) => {
            //     if (isEnterOrSpacePressed(e.key)) {
            //       e.preventDefault()
            //       e.stopPropagation()
            //       props.header.backNavigation(e)
            //     }
            //   }}
            //     onClick={ ( event: any ) => props.header.backNavigation( event ) }>
            //    <Icon icon="LeftArrow" size="medium" version="v2"/>
            // </div>
            <Button
              className="PageLayout__head__backArrow"
              buttonType="tertiary"
              onlyIcon={true}
              ref={layoutHeaderRef}
              icon={'v2-LeftArrow'}
              onlyIconHoverColor='secondary'
              aria-label="Go back"
              testId="cs-page-layout-go-back"
              version="v2"
              onKeyDown={(e: any) => {
                if (isEnterOrSpacePressed(e.key)) {
                  e.preventDefault()
                  e.stopPropagation()
                  props.header.backNavigation(e)
                }
              }}
              onClick={(event: any) => props.header.backNavigation(event)} />
          )}
          {props.header.component !== undefined ? props.header.component : ''}
        </div>
      )
    } else {
      return null
    }
  }, [props.header && props.header.title, props.toggleStat, props.header && props.header.component])
})

const ContentHeader = (props) => {
  return <>{props.contentHeader.component}</>
}

const LeftSidebar = (props: leftSidebarProps) => {
  return <>{props.leftSidebar.component}</>
}

const RightSidebar = (props: rightSidebarProps) => {
  return <>{props.rightSidebar.component}</>
}
const Footer = (props: footerProps) => {
  return <>{props.footer.component}</>
}

const Content = forwardRef((props: contentProps, contentRef: RefObject<HTMLInputElement>) => {

  const [startX, setStartX] = useState(null);
  const [startY, setStartY] = useState(null);

  const leftDrawerRef = useRef(null)

  let cardClassname = ''
  if (props.type === 'card') {
    cardClassname = props.headerType === 'custom' ? 'PageLayout__body--card--custom-header' : 'PageLayout__body--card'
  }

  useEffect(() => {
    const leftDrawer: any = leftDrawerRef?.current
    const version = props.version
    const isResizeable = props.resizeable
    const type = props.type
    const MIN_WIDTH = 70
    const MAX_WIDTH = 800

    let pageLayoutLeftSideBar: any = props?.leftSidebarRef?.current
    let PageLayoutBody: any = contentRef?.current
    let pageLayoutHead: any = props?.layoutHeaderRef?.current
    let pageLayout: any = props?.pageLayoutRef?.current

    let minWidthPx = MIN_WIDTH, maxWidthPx = MAX_WIDTH

    let resizableLeftMinWidth = typeof props?.resizableLeftMinWidth === 'number' ? props?.resizableLeftMinWidth : MIN_WIDTH
    let resizableLeftMaxWidth = typeof props?.resizableLeftMaxWidth === 'number' ? props?.resizableLeftMaxWidth : MAX_WIDTH

    minWidthPx = (resizableLeftMinWidth >= MIN_WIDTH && resizableLeftMinWidth <= MAX_WIDTH) ? resizableLeftMinWidth : (resizableLeftMinWidth < MIN_WIDTH ? MIN_WIDTH : MAX_WIDTH)
    maxWidthPx = (resizableLeftMaxWidth >= minWidthPx && resizableLeftMaxWidth <= MAX_WIDTH) ? resizableLeftMaxWidth : MAX_WIDTH

    function resize(e) {
      if (pageLayoutLeftSideBar?.classList.contains('PageLayout__leftSidebar--show')) {

        pageLayout.classList.add('PageLayout--resizing')

        let calculatedWidthInRem, calculatedWidthInPx;

        const canResize = (xcord: number) => {
          calculatedWidthInPx = props.noSideNav ? xcord : xcord - 55;
          calculatedWidthInRem = pxToRem(calculatedWidthInPx)

          if (calculatedWidthInPx >= minWidthPx && calculatedWidthInPx <= maxWidthPx) {
            return true
          }
          return false
        }

        const calcAndSetPageLayoutWidth = (calculatedWidthInRem: number) => {
          const [headOffset, marginOffset, bodyOffset] = props.noSideNav ? [0, 0, 0] : [3.45, 3.468, 3.62];
          pageLayoutLeftSideBar.style.setProperty('width', `${calculatedWidthInRem}rem`);
          pageLayoutHead.style.setProperty('width', `calc(100% - ${calculatedWidthInRem + headOffset}rem)`);
          PageLayoutBody.style.setProperty('margin-left', `${calculatedWidthInRem + marginOffset}rem`);
          PageLayoutBody.style.setProperty('width', `calc(100% - ${calculatedWidthInRem + bodyOffset}rem)`);
        }

        if (canResize(e.clientX)) {
          calcAndSetPageLayoutWidth(calculatedWidthInRem)
        }
      }
    }
    let moving = false

    const handleMouseDown = () => {
      moving = true
      document.addEventListener('mousemove', resize, false)
      document.addEventListener('mouseup', handleMouseUp, false)
    }

    const handleMouseUp = () => {
      if (moving) {
        document.removeEventListener('mousemove', resize, false)

        pageLayout.classList.remove('PageLayout--resizing')

        let calculatedWidthInPx = remToPx(parseFloat(pageLayoutLeftSideBar.style.width))
        if (calculatedWidthInPx > (maxWidthPx - 10)) {
          leftDrawer.style.cursor = "w-resize"
        } else if (calculatedWidthInPx < (minWidthPx + 10)) {
          leftDrawer.style.cursor = "e-resize"
        } else {
          leftDrawer.style.cursor = "ew-resize"
        }

        if (props.setCurrentWidth) {
          props.setCurrentWidth(calculatedWidthInPx)
        }

        moving = false
      }
      document.removeEventListener('mouseup', handleMouseUp, false)
    }

    const addRemoveResizeMouseEvent = () => {
      leftDrawer.addEventListener('mousedown', handleMouseDown, false)
    }

    if (leftDrawer && version === 'v2' && isResizeable && type === 'list') {
      addRemoveResizeMouseEvent()
    }

    return () => {
      if (leftDrawer && version === 'v2' && isResizeable && type === 'list') {
        leftDrawer?.removeEventListener('mousedown', handleMouseDown, false)
        document?.removeEventListener('mouseup', handleMouseUp, false)
        document?.removeEventListener('mousemove', resize, false)
      }
    }
  }, [])

  const handleMouseDown = (event) => {
    setStartX(event.clientX);
    setStartY(event.clientY);
  };

  const handleMouseUp = (event) => {
    const endX = event.clientX;
    const endY = event.clientY;
    // Check if it was a click (mouse didn't move much)
    const isClick = Math.abs(endX - startX) < 5 && Math.abs(endY - startY) < 5;

    if (isClick) {
      console.log('Mouse click!');
      props.toggle()
    }
  };

  return useMemo(() => {
    if (props.content !== undefined) {
      return (
        <>
          {props.type === 'edit' ? (

            <div ref={contentRef} onKeyDown={props.onKeyDown} className={['PageLayout__body', props.noSideNav ? 'PageLayout__body--no__sideNav' : ''].filter(Boolean).join(' ')} id="PageLayout__body">
              {props.content.livePreview}
              <div className="PageLayout__body__container" >{props.content.component}</div>
            </div>
          ) : (
            <div ref={contentRef} onKeyDown={props.onKeyDown} className={['PageLayout__body', props.noSideNav ? 'PageLayout__body--no__sideNav' : '', cardClassname].filter(Boolean).join(' ')} data-test-id="cs-page-layout-contentBody">
              {props.content.component}
              {
                props.version === 'v2'
                  ? !props.noLeftNav && <div ref={leftDrawerRef} className="PageLayout__body--left-drawer"
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUp}
                    onMouseOver={() => {
                      const hamburgerIcon = document.getElementById('PageLayout-hamburger')
                      hamburgerIcon?.classList?.add('PageLayout__head__hamburger__v2')
                    }}
                    onMouseOut={() => {
                      const hamburgerIcon = document.getElementById('PageLayout-hamburger')
                      hamburgerIcon?.classList?.remove('PageLayout__head__hamburger__v2')
                    }}
                    data-test-id="cs-page-layout-leftDrawer"
                  ></div>
                  : <></>
              }
            </div>
          )}
        </>
      )
    } else {
      return null
    }
  }, [props.content, props.content.component, props.toggleStat, startX, startY])
})

const ListLayout = (props: primaryLayoutProps) => {

  const { testId = "cs-list-layout" } = props;

  const [toggleLeftNav, setLeftNav] = useState(true)
  const [toggleLayout, setToggleLayout] = useState(false)
  const sidebar = useRef(null)
  const contentRef = useRef(null);
  const layoutHeaderRef = useRef(null);
  const pageLayoutRef = useRef(null)

  useEffect(() => {
    if (props.type === 'list' && props.initialWidth && !props.noLeftNav) {//
      const DEFAULT_WIDTH = 240
      const initialWidthInpx = props.initialWidth || DEFAULT_WIDTH
      const initialWidthInRem = pxToRem(initialWidthInpx)

      pageLayoutRef.current.classList.add('PageLayout--resizing')

      sidebar.current.style.width = initialWidthInRem + "rem"
      layoutHeaderRef.current.style.width = `calc(100% - ${initialWidthInRem + 3.45}rem)`
      contentRef.current.style.marginLeft = initialWidthInRem + 3.468 + "rem"
      contentRef.current.style.width = `calc(100% - ${initialWidthInRem + 3.62}rem)`

      const setInitialStyles = () => {
        pageLayoutRef?.current?.classList?.remove('PageLayout--resizing')
      }

      requestAnimationFrame(setInitialStyles)
    }
  }, [])

  const handleToggle = () => {
    setLeftNav(!toggleLeftNav)
    let leftSidebarElem: any = sidebar.current
    if (sidebar.current.classList.contains('PageLayout__leftSidebar--show')) {
      setTimeout(function () {
        leftSidebarElem.style.zIndex = '8'
        leftSidebarElem.style.zIndex = props.version === 'v2' ? 'auto' : '8';
      }, 300)
    } else {
      leftSidebarElem.style.zIndex = '2'
    }
    setToggleLayout(!toggleLayout)
  }
  let contentCardClassname = ''
  if (props.type === 'card') {
    contentCardClassname =
      props.headerType === 'custom' ? 'PageLayout__content--card--custom-header' : 'PageLayout__content--card'
  }
  const noSidenavClassName = props.noSideNav ? "PageLayout--primary--no__sidenav" : ""
  const leftSidebarClassName = !!toggleLeftNav ? 'PageLayout__leftSidebar--show' : 'PageLayout__leftSidebar--hide'
  const leftSidebarBackground = props.hasBackground ? 'PageLayout__leftSidebar--pure__white' : ''
  const noLeftnavClassName = props.noLeftNav ? "PageLayout--primary--no__leftnav" : ""
  const breadcrumbMarginClassName = props.breadcrumbMargin ? "PageLayout--primary--breadcrumb__margin" : ''
  const primaryLayoutClassnames = cn("PageLayout PageLayout--primary", noSidenavClassName, noLeftnavClassName, breadcrumbMarginClassName, props.className, {
    "PageLayout--primary--v2": props.version === "v2"
  })
  const resizeableClassName = (props.version === 'v2' && props.resizeable && props?.type === 'list' && !props.noLeftNav) ? 'PageLayout--resize' : ''

  return (
    <div
      ref={pageLayoutRef}
      data-test-id={testId}
      className={`${primaryLayoutClassnames} ${resizeableClassName}`}>
      {props.leftSidebar !== undefined ? (
        !props.noLeftNav && <div
          ref={sidebar}
          className={`PageLayout__leftSidebar ${breadcrumbMarginClassName} ${leftSidebarBackground} ${leftSidebarClassName}`}
          data-test-id={`${testId}-leftSidebar`}>
          <LeftSidebar leftSidebar={props.leftSidebar} />
        </div>
      ) : null}
      <div
        className={`PageLayout__content ${props.leftSidebar === undefined ? 'PageLayout__content--full' : ''
          } ${contentCardClassname} ${toggleLayout ? 'PageLayout__content--full' : ''}`}>
        {props.leftSidebar !== undefined ? (
          <LayoutHeader
            header={props.header}
            type={props.type}
            headerType={props.headerType}
            toggleStat={toggleLeftNav}
            toggle={handleToggle}
            testId={testId}
            version={props.version}
            ref={layoutHeaderRef}
            noLeftNav={props.noLeftNav}
          />
        ) : (
          <LayoutHeader
            header={props.header}
            headerType={props.headerType}
            toggle={handleToggle}
            type={props.type}
            noLeftNav={props.noLeftNav}
            testId={testId}
          />
        )}
        <Content
          content={props.content}
          type={props.type}
          headerType={props.headerType}
          toggle={handleToggle}
          toggleStat={toggleLeftNav}
          version={props.version}
          resizeable={props.resizeable}
          resizableLeftMaxWidth={props?.resizableLeftMaxWidth}
          resizableLeftMinWidth={props?.resizableLeftMinWidth}
          ref={contentRef}
          layoutHeaderRef={layoutHeaderRef}
          pageLayoutRef={pageLayoutRef}
          leftSidebarRef={sidebar}
          setCurrentWidth={props.setCurrentWidth}
          noLeftNav={props.noLeftNav}
          noSideNav={props.noSideNav}
        />
      </div>
    </div >
  )
}

const RightFooter = forwardRef((props: rightFooterProps, rightFooterRef: any) => {
  const { rightFooter } = props
  return <div className="PageLayout__right-footer" ref={rightFooterRef}>{rightFooter}</div>
})

const LeftFooter = forwardRef((props: leftFooterProps, leftFooterRef: any) => {
  const { leftFooter } = props
  return <div className="PageLayout__left-footer" ref={leftFooterRef}>{leftFooter}</div>
})


const FullLayout = (props: secondaryLayoutProps) => {
  const {
    className,
    header,
    mode,
    leftSidebar,
    rightSidebar,
    contentHeader,
    content,
    footer,
    layoutBackgroundColor,
    rightFooter,
    leftFooter,
    topNavBar,
    version,
    noLeftNav,
    testId = "cs-full-layout"
  } = props

  const contentRef = useRef(null);
  const leftSidebarRef = useRef(null);
  const rightSidebarRef = useRef(null);
  const footerRef = useRef(null);
  const rightFooterRef = useRef(null);
  const leftFooterRef = useRef(null);
  const topNavBarRef = useRef(null);
  const layoutHeaderRef = useRef(null);


  const fullPageClassName = !leftFooter ? `${footer ? '' : 'PageLayout--content-fullpage'}` : ''
  const noSidenavClassName = props.noSideNav ? "PageLayout--secondary--no__sidenav" : ""
  const noLeftnavClassName = props.noLeftNav ? "PageLayout--secondary--no__leftnav" : ""
  const breadcrumbMarginClassName = props.breadcrumbMargin && (mode === undefined || mode === 'compact') ? "PageLayout--secondary--breadcrumb__margin" : ''
  const secondaryLayoutClassnames = cn('PageLayout PageLayout--secondary', noSidenavClassName, noLeftnavClassName, breadcrumbMarginClassName, className, {
    'PageLayout--full-screen': mode === 'fullscreen',
    'PageLayout--content-fullpage': fullPageClassName,
    'PageLayout--secondary-edit': layoutBackgroundColor === true,
    'PageLayout--secondary-edit--v2': version,
    'PageLayout--secondary-edit-content-header': contentHeader,
  })

  const handleContentKeyUp = (e) => {
    if (e.altKey && e.shiftKey) {
      if (isArrowUpPressed(e.key)) {
        e.preventDefault();
        if (topNavBarRef?.current) {
          setRequiredFocus(topNavBarRef)
        } else if (layoutHeaderRef?.current) {
          setRequiredFocus(layoutHeaderRef)
        }
      }
      if (isArrowDownPressed(e.key)) {
        e.preventDefault();
        if (footerRef?.current) {
          setRequiredFocus(footerRef)
        }
        else if (leftFooterRef?.current) {
          setRequiredFocus(leftFooterRef)
        }
      }
    } else if (e.metaKey && e.key === '/') {
      e.preventDefault();
      if (rightSidebarRef.current && rightSidebarRef.current.children.length !== 0) {
        setRequiredFocus(rightSidebarRef);
      } else if (leftSidebarRef.current && leftSidebarRef.current.children.length !== 0) {
        setRequiredFocus(leftSidebarRef);
      }
    }
  }

  const handleLeftFooterKeyUp = (e) => {
    if (e.altKey && e.shiftKey) {
      if (isArrowRightPressed(e.key)) {
        e.preventDefault();
        setRequiredFocus(rightFooterRef);
      }
      if (isArrowLeftPressed(e.key)) {
        e.preventDefault();
        setRequiredFocus(leftSidebarRef);
      }
      if (isArrowUpPressed(e.key)) {
        e.preventDefault();
        setRequiredFocus(contentRef);
      }
    }
  }

  const handleRightFooterKeyUp = (e) => {
    if (e.altKey && e.shiftKey) {
      if (isArrowRightPressed(e.key)) {
        e.preventDefault();
        setRequiredFocus(rightSidebarRef);
      }
      if (isArrowLeftPressed(e.key)) {
        e.preventDefault();
        setRequiredFocus(leftFooterRef);
      }
      if (isArrowUpPressed(e.key)) {
        e.preventDefault();
        setRequiredFocus(contentRef);
      }
    }
  }

  const handleFooterKeyUp = (e) => {
    if (e.altKey && e.shiftKey) {
      if (isArrowRightPressed(e.key)) {
        e.preventDefault();
        setRequiredFocus(rightSidebarRef);
      }
      if (isArrowLeftPressed(e.key)) {
        e.preventDefault();
        setRequiredFocus(leftSidebarRef);
      }
      if (isArrowUpPressed(e.key)) {
        e.preventDefault();
        setRequiredFocus(contentRef);
      }
    }
  }

  const handleTopNavBarKeyUp = (e) => {
    if (e.altKey && e.shiftKey) {
      if (isArrowRightPressed(e.key)) {
        e.preventDefault();
        setRequiredFocus(rightSidebarRef);
      }
      if (isArrowLeftPressed(e.key)) {
        e.preventDefault();
        setRequiredFocus(leftSidebarRef);
      }
      if (isArrowDownPressed(e.key)) {
        e.preventDefault();
        setRequiredFocus(contentRef);
      }
      if (isArrowUpPressed(e.key)) {
        e.preventDefault();
        if (layoutHeaderRef?.current) {
          setRequiredFocus(layoutHeaderRef);
        }
      }
    }
  }

  const handleLayoutHeaderKeyUp = (e) => {
    if (e.altKey && e.shiftKey) {
      if (isArrowRightPressed(e.key)) {
        e.preventDefault();
        setRequiredFocus(rightSidebarRef);
      }
      if (isArrowLeftPressed(e.key)) {
        e.preventDefault();
        setRequiredFocus(leftSidebarRef);
      }
      if (isArrowDownPressed(e.key)) {
        e.preventDefault();
        if (topNavBarRef?.current) {
          setRequiredFocus(topNavBarRef);
        } else if (contentRef?.current) {
          setRequiredFocus(contentRef);
        }
      }
    }
  }

  const handleLeftSideBarKeyUp = (e) => {
    if (e.altKey && e.shiftKey) {
      if (isArrowDownPressed(e.key)) {
        e.preventDefault()
        if (footerRef?.current) {
          setRequiredFocus(footerRef)
        } else if (leftFooterRef?.current) {
          setRequiredFocus(leftFooterRef)
        } else if (rightSidebarRef?.current) {
          setRequiredFocus(rightSidebarRef)
        }
      }
      if (isArrowUpPressed(e.key)) {
        e.preventDefault()
        if (topNavBarRef?.current) {
          setRequiredFocus(topNavBarRef)
        } else if (layoutHeaderRef?.current) {
          setRequiredFocus(layoutHeaderRef)
        }
      }
    } else if (e.metaKey && e.key === '/') {
      e.preventDefault()
      setRequiredFocus(contentRef)
    }
  }

  const handleRightSideBarKeyUp = (e) => {
    if (e.altKey && e.shiftKey) {
      if (isArrowDownPressed(e.key)) {
        e.preventDefault()
        if (footerRef?.current) {
          setRequiredFocus(footerRef)
        } else if (rightSidebarRef?.current) {
          setRequiredFocus(rightSidebarRef)
        } else if (leftFooterRef?.current) {
          setRequiredFocus(leftFooterRef)
        }
      }
      if (isArrowUpPressed(e.key)) {
        e.preventDefault()
        if (topNavBarRef?.current) {
          setRequiredFocus(topNavBarRef)
        } else if (layoutHeaderRef?.current) {
          setRequiredFocus(layoutHeaderRef)
        }
      }
    } else if (e.metaKey && e.key === '/') {
      e.preventDefault();
      if (leftSidebarRef.current && leftSidebarRef.current.children.length !== 0) {
        setRequiredFocus(leftSidebarRef)
      } else if (contentRef.current) {
        setRequiredFocus(contentRef)
      }
    }
  }

  return (
    <div data-test-id={testId} className={secondaryLayoutClassnames}>
      <LayoutHeader onKeyDown={handleLayoutHeaderKeyUp} ref={layoutHeaderRef} header={header} type={'edit'} mode={mode} />
      <div className={`PageLayout__content`} >
        {leftSidebar && (
          !noLeftNav && <div className={`PageLayout__leftSidebar`} tabIndex={-1} ref={leftSidebarRef} onKeyDown={handleLeftSideBarKeyUp}>
            <LeftSidebar leftSidebar={leftSidebar} />
          </div>
        )}
        {rightSidebar && (
          <div className={`PageLayout__rightSidebar`} tabIndex={-1} ref={rightSidebarRef} onKeyDown={handleRightSideBarKeyUp}>
            <RightSidebar rightSidebar={rightSidebar} />
          </div>
        )}
        {
          contentHeader && (
            <div className='PageLayout__content__header'>
              <ContentHeader contentHeader={contentHeader} />
            </div>
          )
        }
        {topNavBar && (
          <div className={`PageLayout__topNavbar`} tabIndex={-1} ref={topNavBarRef} onKeyDown={handleTopNavBarKeyUp}>
            <TopNavbar topNavBar={topNavBar} />
          </div>
        )}
        <Content onKeyDown={handleContentKeyUp} ref={contentRef} content={content} type={'edit'} mode={mode} />
        {footer && (
          <div className={`PageLayout__footer`} tabIndex={-1} ref={footerRef} onKeyDown={handleFooterKeyUp}>
            <Footer footer={footer} />
          </div>
        )}
        {rightFooter && <RightFooter onKeyDown={handleRightFooterKeyUp} ref={rightFooterRef} rightFooter={rightFooter} />}
        {leftFooter && <LeftFooter onKeyDown={handleLeftFooterKeyUp} ref={leftFooterRef} leftFooter={leftFooter} />}
      </div>
    </div>
  )
}

const EmptyPage = (props: emptyPageProps) => {

  const { testId = "cs-empty-page" } = props;

  const PageHeaderWithCSLogo = () => (
    <div className="PageLayout__head--emptyLayout" data-test-id={testId}>
      <div
        className={`PageLayout__head--CSLogo ${props.header?.headerLogoOnClick ? 'clickable' : ''}`}
        onClick={props.header?.headerLogoOnClick}>
        <Icon icon="ContentstackLogoGrey" />
      </div>
    </div>
  )

  return (
    <div
      data-test-id={testId}
      className={`PageLayout--emptyLayout
        ${props.content.backgroundColor === 'secondary' ? 'secondaryBackground' : 'primaryBackground'}`}>
      <PageHeaderWithCSLogo />
      <div
        className={`${props.content.alignContent === 'center' ? 'PageLayout__content--center' : 'PageLayout__content'
          }`}>
        {props.content.component}
      </div>
    </div>
  )
}

const PageLayout = (props: layoutProps) => {

  const {
    header,
    leftSidebar,
    content,
    type,
    rightSidebar,
    contentHeader,
    footer,
    headerType,
    className,
    mode,
    layoutBackgroundColor,
    rightFooter,
    leftFooter,
    topNavBar,
    version,
    testId = "cs-page-layout",
    hasBackground = false,
    resizeable,
    resizableLeftMaxWidth,
    resizableLeftMinWidth,
    noSideNav,
    initialWidth,
    setCurrentWidth,
    noLeftNav,
    breadcrumbMargin
  } = props

  switch (type) {
    case 'list':
      return (
        <ListLayout header={header} leftSidebar={leftSidebar} content={content} className={className} testId={testId} hasBackground={hasBackground} noSideNav={noSideNav} version={version} resizeable={resizeable} type={type} resizableLeftMinWidth={resizableLeftMinWidth} resizableLeftMaxWidth={resizableLeftMaxWidth} initialWidth={initialWidth} setCurrentWidth={setCurrentWidth} noLeftNav={noLeftNav} breadcrumbMargin={breadcrumbMargin} />
      )
    case 'card':
      return (
        <ListLayout
          header={header}
          headerType={headerType}
          leftSidebar={leftSidebar}
          content={content}
          type={type}
          noSideNav={noSideNav}
          className={className}
          testId={testId}
          noLeftNav={noLeftNav}
          breadcrumbMargin={breadcrumbMargin}
        />
      )
    case 'edit':
      return (
        <FullLayout
          header={header}
          leftSidebar={leftSidebar}
          content={content}
          rightSidebar={rightSidebar}
          contentHeader={contentHeader}
          footer={footer}
          className={className}
          mode={mode}
          layoutBackgroundColor={layoutBackgroundColor}
          rightFooter={rightFooter}
          leftFooter={leftFooter}
          topNavBar={topNavBar}
          version={version}
          testId={testId}
          noLeftNav={noLeftNav}
          noSideNav={noSideNav}
          breadcrumbMargin={breadcrumbMargin}
        />
      )
    case 'empty':
      return <EmptyPage content={content} header={header} testId={testId} />
    default:
      return null
  }
}

export default PageLayout
